import { Component, OnInit, inject, model, signal } from '@angular/core';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { Observable } from 'rxjs';

import { BaseComponent } from '../../base/base.component';
import { GridService } from '../../../services/component-services/grid.service';
import { ModalService, NotificationService } from '../../../services';
import { ImportConfirmState, ImportPreview } from '../../../models';
import { importConfirmFailure } from '../../../helpers/import-confirm-helpers';

@Component({
  template: '',
})
export abstract class BaseImportPreviewModalComponent<T extends ImportPreview> extends BaseComponent implements OnInit {
  protected readonly gridService = inject(GridService);
  protected readonly modalService = inject(ModalService);
  protected readonly notificationService = inject(NotificationService);

  public readonly previewData = model<T[]>([]);

  /** Bound to `app-import-preview-modal` as `[state]`. */
  protected readonly importState = signal<ImportConfirmState>({ status: 'ready' });

  protected columnDefs: ColDef[] = [];
  protected rowData: T[] = [];

  protected validCount = 0;
  protected invalidCount = 0;
  protected warningCount = 0;
  protected newCount = 0;
  protected updateCount = 0;

  protected abstract readonly title: string;

  ngOnInit() {
    this.columnDefs = [...this.getCommonColumns(), ...this.getFeatureColumns()];
    this.rowData = this.previewData();
    this.calculateSummary();
  }

  get hasErrors(): boolean {
    return this.invalidCount > 0;
  }

  get hasValidRows(): boolean {
    return this.validCount > 0;
  }

  protected abstract getFeatureColumns(): ColDef[];

  protected abstract doConfirmImport(validRows: T[]): Observable<any>;

  /**
   * ⚠️ One request, and the server finishes it whatever happens to the connection - so the modal
   * only reports an outcome the API actually gave (see `importConfirmFailure`). While it is in flight
   * Confirm and Cancel are locked; once the outcome is unknown, Confirm stays locked and Close is the
   * only way out, so the same file cannot be imported twice with one more click (M&N, Oct 2026).
   */
  confirmImport() {
    const status = this.importState().status;
    if (status === 'importing' || status === 'unknown') {
      return;
    }

    const validRows = this.rowData.filter((row) => row.isValid);
    this.importState.set({ status: 'importing' });
    let answered = false;
    this.doConfirmImport(validRows)
      .pipe(this.untilDestroyed())
      .subscribe({
        next: (response: unknown) => {
          answered = true;
          this.importState.set({ status: 'ready' });
          this.notificationService.success(this.successMessage(response));
          this.modalService.close(true);
        },
        error: (error: unknown) => {
          answered = true;
          this.importState.set(this.outcomeState(importConfirmFailure(error)));
        },
        complete: () => {
          // The request ended with no answer and no error (an interceptor swallowed it), so the
          // import never ran.
          if (!answered) {
            this.importState.set(this.outcomeState({ status: 'failed' }));
          }
        },
      });
  }

  /** The toast once the import has gone through. An import whose API reports more says so here. */
  protected successMessage(_response: unknown): string {
    return $localize`Import completed successfully`;
  }

  /** The message for a failure the API reported. Its own text is shown under it. */
  protected failureMessage(): string {
    return $localize`Import failed`;
  }

  private outcomeState(outcome: ImportConfirmState): ImportConfirmState {
    switch (outcome.status) {
      case 'unknown':
        return {
          status: 'unknown',
          message: $localize`The import is taking longer than expected and is still running on the server. Do not import this file again — check the results in a few minutes.`,
        };
      case 'refused':
        // The API's refusal, already in the user's language, is the message.
        return { status: 'refused', message: outcome.detail || $localize`The import was refused.` };
      default:
        return { status: 'failed', message: this.failureMessage(), detail: outcome.detail };
    }
  }

  private getCommonColumns(): ColDef[] {
    return [
      this.gridService.number('rowNumber', $localize`Row`, { width: 70 }),
      this.gridService.text('status', $localize`Status`, {
        width: 100,
        valueGetter: (params: any) => {
          if (!params.data.isValid) return 'Error';
          if (params.data.warnings?.length > 0) return 'Warning';
          return params.data.isExisting ? 'Update' : 'New';
        },
        cellClass: (params: any) => {
          if (!params.data.isValid) return 'text-danger fw-bold';
          if (params.data.warnings?.length > 0) return 'text-warning fw-bold';
          if (params.data.isExisting) return 'text-primary';
          return 'text-success';
        },
      }),
      this.gridService.text('errorsSummary', $localize`Errors`, {
        width: 200,
        valueGetter: (params: any) => (params.data.errors || []).join('; '),
        cellClass: 'text-danger',
      }),
      this.gridService.text('warningsSummary', $localize`Warnings`, {
        width: 200,
        valueGetter: (params: any) => (params.data.warnings || []).join('; '),
        cellClass: 'text-warning',
      }),
    ];
  }

  private calculateSummary() {
    this.validCount = this.rowData.filter((row) => row.isValid).length;
    this.invalidCount = this.rowData.filter((row) => !row.isValid).length;
    this.warningCount = this.rowData.filter((row) => row.warnings?.length > 0).length;
    this.newCount = this.rowData.filter((row) => row.isValid && !row.isExisting).length;
    this.updateCount = this.rowData.filter((row) => row.isValid && row.isExisting).length;
  }
}
